import { EstateType } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { useCallback, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CharacterMpProvider from "@/core/data-provider/data-mp/character/character-mp-provider";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import {
  setSlotsOwnerEstates,
  setSlotsSettings,
  setSlotsTenantEstates,
} from "@/domains/estate/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useLoadSlots() {
  const { t } = useTranslation("estate");
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);

  const loadSettings = useCallback(async (): Promise<typeof response> => {
    const response = await CharacterMpProvider.getEstateCapacity();
    return response;
  }, []);

  const loadEstates = useCallback(async (): Promise<typeof response> => {
    const response = await EstateMpProvider.getRelated();
    return response;
  }, []);

  const loadSlots = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsResponse, estatesResponse] = await Promise.all([
        loadSettings(),
        loadEstates(),
      ]);

      const hasSettingsError = settingsResponse instanceof RpcMpProviderFailedResponse;
      const hasEstatesError = estatesResponse instanceof RpcMpProviderFailedResponse;

      if (hasSettingsError || hasEstatesError) {
        if (hasSettingsError) {
          toast.error(t("estate.slots.errors.notLoadEstateSettings"));
          console.log(
            "Failed fetch loadSettings() - CharacterMpProvider.getEstateCapacity()",
            settingsResponse,
          );
        }

        if (hasEstatesError) {
          toast.error(t("estate.slots.errors.notLoadEstateList"));
          console.log("Failed fetch loadSlots() - EstateMpProvider.getRelated()", estatesResponse);
        }

        dispatch(
          setSlotsSettings({
            ownerCount: 0,
            tenantCount: 0,
            maxCount: 0,
            price: 0,
          }),
        );
        dispatch(setSlotsOwnerEstates([]));
        dispatch(setSlotsTenantEstates([]));
        return;
      }

      dispatch(
        setSlotsSettings({
          maxCount: settingsResponse.data.maxCount,
          ownerCount: settingsResponse.data.ownerCount,
          price: settingsResponse.data.price,
          tenantCount: settingsResponse.data.tenantCount,
        }),
      );

      const ownerEstates = [];
      const tenantEstates = [];
      estatesResponse.data.items.forEach((item) => {
        if (item.entity.type === EstateType.HotelRoom) {
          return;
        }

        const estateInfo = {
          id: item.id,
          type: item.entity.type,
          complexId:
            item.entity.type === EstateType.Apartment ? item.entity.content.complexId : null,
          address: `${item.address.streetName} ${item.address.index} ${item.entity.type === EstateType.Apartment ? item.entity.content.room : ""}`,
          level: item.level,
          isResident: item.hasResidence,
        };

        if (item.isOwner) {
          ownerEstates.push(estateInfo);
        }

        if (item.isTenant) {
          tenantEstates.push(estateInfo);
        }
      });

      dispatch(setSlotsOwnerEstates(ownerEstates));
      dispatch(setSlotsTenantEstates(tenantEstates));
    } catch (error) {
      console.error("Unexpected error loading data:", error);
      toast.error(t("estate.slots.errors.notLoadData"));
      dispatch(
        setSlotsSettings({
          ownerCount: 0,
          tenantCount: 0,
          maxCount: 0,
          price: 0,
        }),
      );
      dispatch(setSlotsOwnerEstates([]));
      dispatch(setSlotsTenantEstates([]));
    } finally {
      setLoading(false);
    }
  }, [dispatch, t, loadEstates, loadSettings]);

  useLayoutEffect(() => {
    loadSlots();
  }, [loadSlots]);

  return {
    loading,
    loadSlots,
  };
}
